/**
 * `usePlatformServiceOrders` — live snapshot of IO Service orders.
 *
 * Two modes:
 *   - `scope: 'studio'` + `orgId` — list this studio's own orders
 *     for the "My requests" page. Permission-safe for non-admins.
 *   - `scope: 'admin'` — list every order across every org for the
 *     `/admin/io-orders` queue. Requires the caller to have the
 *     `role: 'app_owner'` custom claim; Firestore will reject the
 *     query otherwise.
 *
 * Sort: createdAt desc (newest first). Client-side because not all
 * deployments have a composite index covering every status filter
 * variant yet.
 */
import {
  collection,
  onSnapshot,
  query,
  where,
} from 'firebase/firestore';
import { useEffect, useMemo, useState } from 'react';

import { db } from '@/lib/firebase';
import type {
  PlatformServiceOrder,
  PlatformServiceOrderStatus,
} from '@/lib/platformServiceOrders';

export type UsePlatformServiceOrdersResult = {
  data: PlatformServiceOrder[];
  loading: boolean;
};

export type UsePlatformServiceOrdersOptions = {
  scope: 'studio' | 'admin';
  /** Required when scope === 'studio'. Ignored when scope === 'admin'. */
  orgId?: string | null;
  /** Optional status filter (client-side). */
  status?: PlatformServiceOrderStatus | null;
  /** Optional substring search across service title, org name,
   *  contact name, contact phone. */
  searchText?: string;
};

export function usePlatformServiceOrders(
  opts: UsePlatformServiceOrdersOptions,
): UsePlatformServiceOrdersResult {
  const { scope, orgId, status, searchText } = opts;

  const [all, setAll] = useState<PlatformServiceOrder[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Studio mode requires an orgId — bail with empty until ready.
    if (scope === 'studio' && !orgId) {
      setAll([]);
      setLoading(false);
      return;
    }
    setLoading(true);

    const ref =
      scope === 'studio'
        ? query(
            collection(db, 'platformServiceOrders'),
            where('orgId', '==', orgId),
          )
        : collection(db, 'platformServiceOrders');

    return onSnapshot(
      ref,
      (snap) => {
        const rows: PlatformServiceOrder[] = snap.docs.map((d) => ({
          id: d.id,
          ...(d.data() as Omit<PlatformServiceOrder, 'id'>),
        }));
        rows.sort((a, b) => {
          const am = a.createdAt?.toMillis?.() ?? 0;
          const bm = b.createdAt?.toMillis?.() ?? 0;
          return bm - am;
        });
        setAll(rows);
        setLoading(false);
      },
      (err) => {
        console.warn('[usePlatformServiceOrders] snapshot error:', err);
        setAll([]);
        setLoading(false);
      },
    );
  }, [scope, orgId]);

  const filtered = useMemo(() => {
    const q = (searchText ?? '').trim().toLowerCase();
    return all.filter((it) => {
      if (status && it.status !== status) return false;
      if (q) {
        const blob = [
          it.serviceTitle,
          it.orgName,
          it.contactName,
          it.contactPhone,
          it.notes ?? '',
        ]
          .join(' ')
          .toLowerCase();
        if (!blob.includes(q)) return false;
      }
      return true;
    });
  }, [all, status, searchText]);

  return { data: filtered, loading };
}
